// ── RevenueCalendar — 일별 매출 heatmap ──
// 거래장부 transactions에서 type === 'income' 행 모아 일자별 합산
// 색상 강도: 매출액 비례 (max 기준 상대값)
// 미입력일 = 회색
import React, { useMemo, useState } from 'react';
import { L, M, trLang } from '../i18n/L';   // V160 중국어

const KOR_DAYS = ['일', '월', '화', '수', '목', '금', '토'];
const ZH_DAYS = ['日', '一', '二', '三', '四', '五', '六'];   // V160
const fmtWon = (n) => M(n);   // V160 매장 통화 (중국 매장은 ¥)

function pad(n) { return String(n).padStart(2, '0'); }

export default function RevenueCalendar({
  transactions = [],
  monthKey,           // 'YYYY-MM'
  isMobile,
  onClickDay,         // (dateStr 'YYYY-MM-DD') => void
  onAddRevenue,       // () => void  (헤더 ✏️ 버튼)
}) {
  const [hovered, setHovered] = useState(null);  // { day, amount, count }

  // ── 일별 매출 집계 ──
  const dayMap = useMemo(() => {
    const m = {};
    if (!Array.isArray(transactions) || !monthKey) return m;
    for (const t of transactions) {
      if (t?.type !== 'income') continue;
      if (!t.date || !t.date.startsWith(monthKey)) continue;
      const day = parseInt(t.date.slice(8, 10), 10);
      if (!day || day < 1 || day > 31) continue;
      const amt = (Number(t.supply) || 0) + (Number(t.vat) || 0);
      if (!m[day]) m[day] = { amount: 0, count: 0 };
      m[day].amount += amt;
      m[day].count += 1;
    }
    return m;
  }, [transactions, monthKey]);

  const totalRevenue = useMemo(
    () => Object.values(dayMap).reduce((s, v) => s + v.amount, 0),
    [dayMap]
  );
  const inputDays = Object.keys(dayMap).length;

  // monthKey → year, month
  const [yStr, mStr] = monthKey.split('-');
  const year = parseInt(yStr, 10);
  const month = parseInt(mStr, 10);

  // 해당 월의 마지막 날
  const lastDay = new Date(year, month, 0).getDate();
  // 1일의 요일 (0=일)
  const firstDow = new Date(year, month - 1, 1).getDay();

  // 매출 max (색상 정규화용)
  const maxAmount = Math.max(...Object.values(dayMap).map(v => v.amount), 1);

  // 6 weeks × 7 days 그리드
  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push({ empty: true, key: `pre-${i}` });
  for (let d = 1; d <= lastDay; d++) {
    const data = dayMap[d];
    const intensity = data ? Math.min(1, data.amount / maxAmount) : 0;
    cells.push({
      day: d,
      amount: data?.amount || 0,
      count: data?.count || 0,
      hasData: !!data,
      intensity,
      isToday: (() => {
        const today = new Date();
        return today.getFullYear() === year
          && today.getMonth() + 1 === month
          && today.getDate() === d;
      })(),
      key: `d-${d}`,
    });
  }
  while (cells.length % 7 !== 0) cells.push({ empty: true, key: `post-${cells.length}` });

  // 색상 — 강도에 따라
  const colorFor = (intensity) => {
    if (intensity === 0) return '#F4F4F5';
    // 흰색 → #FF6F0F 그라데이션
    const r = Math.round(255 - 21 * intensity);    // 255→234
    const g = Math.round(237 - 149 * intensity);   // 237→88
    const b = Math.round(213 - 201 * intensity);   // 213→12
    return `rgb(${r}, ${g}, ${b})`;
  };
  const textColorFor = (intensity) => intensity >= 0.5 ? '#fff' : '#374151';

  return (
    <div style={{
      background: '#fff',
      borderRadius: 16,
      padding: isMobile ? '16px 14px' : '18px 22px',
      marginBottom: 18,
      boxShadow: '0 3px 18px rgba(0,0,0,0.08)',
    }}>
      {/* 헤더 */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 14, flexWrap: 'wrap', gap: 10,
      }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 800, color: '#1a1a1a' }}>
            {L("🗓️ 이달 매출 캘린더")}
          </div>
          <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
            {L("거래장부 입력 기준 · 색이 진할수록 매출↑")}
          </div>
        </div>
        <div style={{ textAlign: 'right', display: 'flex', alignItems: 'center', gap: 10 }}>
          <div>
            <div style={{ fontSize: 11, color: '#9ca3af' }}>{L("이달 합계")}</div>
            <div style={{ fontSize: 16, fontWeight: 900, color: '#18181B' }}>
              {fmtWon(totalRevenue)}
            </div>
            <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 1 }}>
              {L("{0}일 입력 / {1}일 중", inputDays, lastDay)}
            </div>
          </div>
          {onAddRevenue && (
            <button onClick={onAddRevenue} title={L("빠른 매출 입력")} style={{
              padding: '8px 12px',
              borderRadius: 8,
              border: 'none',
              background: '#EA580C',
              color: '#fff',
              fontSize: 12,
              fontWeight: 800,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              boxShadow: '0 2px 8px rgba(30,107,60,0.25)',
            }}>
              {L("✏️ 입력")}
            </button>
          )}
        </div>
      </div>

      {/* 요일 헤더 */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)',
        gap: 4, marginBottom: 4,
      }}>
        {(trLang() === 'zh' ? ZH_DAYS : KOR_DAYS).map((d, i) => (
          <div key={d} style={{
            fontSize: 10, fontWeight: 700, textAlign: 'center',
            color: i === 0 ? '#52525B' : i === 6 ? '#2563eb' : '#9ca3af',
            padding: '4px 0',
          }}>{d}</div>
        ))}
      </div>

      {/* 캘린더 그리드 */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)',
        gap: 4,
      }}>
        {cells.map(c => {
          if (c.empty) return <div key={c.key} style={{ aspectRatio: '1 / 1' }} />;
          const bg = colorFor(c.intensity);
          const tc = textColorFor(c.intensity);
          return (
            <div key={c.key}
              onMouseEnter={() => c.hasData && setHovered({ day: c.day, amount: c.amount, count: c.count })}
              onMouseLeave={() => setHovered(null)}
              onClick={() => onClickDay && onClickDay(`${monthKey}-${String(c.day).padStart(2, '0')}`)}
              title={c.hasData ? L("{0}/{1} — {2} ({3}건) · 클릭해서 추가 입력", month, c.day, fmtWon(c.amount), c.count) : L("{0}/{1} — 클릭해서 매출 입력", month, c.day)}
              style={{
                aspectRatio: '1 / 1',
                background: bg,
                border: c.isToday ? '2px solid #FF6F0F' : '1px solid #e5e7eb',
                borderRadius: 6,
                padding: '4px',
                display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
                cursor: onClickDay ? 'pointer' : (c.hasData ? 'pointer' : 'default'),
                position: 'relative',
                transition: 'transform 0.1s',
              }}
            >
              <div style={{
                fontSize: 11, fontWeight: c.isToday ? 900 : 600, color: tc,
                textAlign: 'right',
              }}>
                {c.day}
              </div>
              {c.hasData && (
                <div style={{
                  fontSize: isMobile ? 8 : 9, fontWeight: 700, color: tc,
                  textAlign: 'center', lineHeight: 1.1,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {c.amount >= 1000000
                    ? `${(c.amount / 1000000).toFixed(1)}M`
                    : c.amount >= 10000
                    ? L("{0}만", Math.round(c.amount / 10000))
                    : `${Math.round(c.amount / 1000)}k`}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* 호버 디테일 */}
      {hovered && (
        <div style={{
          marginTop: 10, padding: '8px 12px', background: '#f8fafc',
          borderRadius: 8, fontSize: 12, color: '#374151',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <span><strong style={{ color: '#18181B' }}>{L("{0}월 {1}일", month, hovered.day)}</strong></span>
          <span>{L("매출")} <strong>{fmtWon(hovered.amount)}</strong> {L("· 거래 {0}건", hovered.count)}</span>
        </div>
      )}

      {/* 범례 */}
      <div style={{
        marginTop: 12, display: 'flex', alignItems: 'center', gap: 8,
        fontSize: 10, color: '#9ca3af',
      }}>
        <span>{L("매출 적음")}</span>
        {[0, 0.25, 0.5, 0.75, 1].map(i => (
          <div key={i} style={{
            width: 14, height: 14, borderRadius: 3,
            background: colorFor(i), border: '1px solid #e5e7eb',
          }} />
        ))}
        <span>{L("많음")}</span>
        {totalRevenue === 0 && (
          <span style={{ marginLeft: 'auto', color: '#9ca3af', fontStyle: 'italic' }}>
            {L("거래장부에 매출 입력이 없어요")}
          </span>
        )}
      </div>
    </div>
  );
}
